import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Menu, ShoppingCart, User, X } from "lucide-react";
import Logo from "@/components/Logo";
import SmartSearch from "@/components/SmartSearch";
import { DynamicIcon } from "@/lib/icons";
import { useCategories } from "@/hooks/useCatalog";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { useSiteStatus } from "@/context/SiteStatusContext";
import { CURRENCY_CONFIG } from "@/lib/format";

const QUICK_LINKS = [
  { label: "Accueil", to: "/" },
  { label: "Boutique", to: "/boutique" },
  { label: "🎓 Formations", to: "/formations" },
  { label: "🎁 Offres groupées", to: "/offres" },
  { label: "Promotions", to: "/promotions" },
  { label: "Nos services", to: "/services" },
  { label: "Déblocage & Services", to: "/deblocage" },
  { label: "🏪 Boutiques", to: "/boutiques" },
  { label: "💼 Devenir revendeur", to: "/devenir-revendeur" },
  { label: "Support & FAQ", to: "/support" },
];

export function MobileHeader() {
  const [open, setOpen] = useState(false);
  const { data: categories = [] } = useCategories();
  const { cartCount, currency, setCurrency } = useStore();
  const { user, logout } = useAuth();
  const { resellers } = useSiteStatus();
  const resellersOn = resellers?.enabled !== false;
  const navigate = useNavigate();

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b border-line bg-white/95 backdrop-blur-md lg:hidden">
        <div className="flex h-14 items-center gap-3 px-4">
          <button
            data-testid="mobile-menu-btn"
            aria-label="Menu"
            onClick={() => setOpen(true)}
            className="-ml-1 flex h-9 w-9 items-center justify-center rounded-lg text-ink transition-colors duration-200 active:bg-slate-100"
          >
            <Menu className="h-6 w-6" strokeWidth={2.2} />
          </button>

          <div className="flex-1">
            <Logo compact />
          </div>

          <Link
            to={user ? "/compte" : "/connexion"}
            data-testid="mobile-account-btn"
            aria-label="Compte"
            className="flex h-9 w-9 items-center justify-center rounded-lg text-ink transition-colors duration-200 active:bg-slate-100"
          >
            <User className="h-[22px] w-[22px]" strokeWidth={1.9} />
          </Link>

          <Link
            to="/panier"
            data-testid="mobile-cart-btn"
            aria-label="Panier"
            className="relative flex h-9 w-9 items-center justify-center rounded-lg text-ink transition-colors duration-200 active:bg-slate-100"
          >
            <ShoppingCart className="h-[22px] w-[22px]" strokeWidth={1.9} />
            <span className="absolute -right-0.5 -top-0.5 flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-brand px-1 text-[10px] font-bold text-white">
              {cartCount}
            </span>
          </Link>
        </div>
        <div className="px-4 pb-3">
          <SmartSearch variant="mobile" />
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-[80] lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-ink/50 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-[86%] max-w-[340px] overflow-y-auto bg-white shadow-2xl animate-fade-up">
            <div className="flex items-center justify-between border-b border-line px-4 py-4">
              <Logo />
              <button
                data-testid="mobile-drawer-close"
                aria-label="Fermer"
                onClick={() => setOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 active:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-4 py-4">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Navigation</p>
              <div className="flex flex-col">
                {QUICK_LINKS
                  .filter((link) => resellersOn || !["/boutiques", "/devenir-revendeur"].includes(link.to))
                  .map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    data-testid={`drawer-link-${link.to.replace("/", "") || "home"}`}
                    onClick={() => setOpen(false)}
                    className="rounded-lg px-2 py-2.5 text-[15px] font-semibold text-ink transition-colors duration-200 active:bg-brand-soft"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>

            <div className="border-t border-line px-4 py-4">
              <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Catégories</p>
              <div className="grid grid-cols-2 gap-2">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/boutique?categorie=${cat.slug}`}
                    data-testid={`drawer-category-${cat.slug}`}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 rounded-xl border border-line p-2.5 transition-colors duration-200 active:bg-brand-soft"
                  >
                    <span
                      className="flex h-8 w-8 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${cat.color}14`, color: cat.color }}
                    >
                      <DynamicIcon name={cat.icon} className="h-4 w-4" />
                    </span>
                    <span className="text-xs font-semibold leading-tight text-ink">{cat.name}</span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="border-t border-line px-4 py-4">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">Devise</p>
              <div className="flex gap-2">
                {Object.keys(CURRENCY_CONFIG).map((code) => (
                  <button
                    key={code}
                    data-testid={`drawer-currency-${code}`}
                    onClick={() => setCurrency(code)}
                    className={`flex-1 rounded-lg border px-3 py-2 text-xs font-bold transition-colors duration-200 ${
                      currency === code ? "border-brand bg-brand text-white" : "border-line text-ink"
                    }`}
                  >
                    {code}
                  </button>
                ))}
              </div>
            </div>

            <div className="border-t border-line px-4 py-4 pb-10">
              {user ? (
                <>
                  <Link
                    to="/compte"
                    onClick={() => setOpen(false)}
                    data-testid="drawer-account"
                    className="mb-2 block rounded-xl bg-brand px-4 py-3 text-center text-sm font-bold text-white"
                  >
                    Mon espace client
                  </Link>
                  {user.role === "vendor" && (
                    <Link
                      to="/vendeur/dashboard"
                      onClick={() => setOpen(false)}
                      data-testid="drawer-vendor"
                      className="mb-2 block rounded-xl border border-brand px-4 py-3 text-center text-sm font-bold text-brand"
                    >
                      Espace vendeur
                    </Link>
                  )}
                  {user.role === "admin" && (
                    <Link
                      to="/admin"
                      onClick={() => setOpen(false)}
                      data-testid="drawer-admin"
                      className="mb-2 block rounded-xl border border-brand px-4 py-3 text-center text-sm font-bold text-brand"
                    >
                      Administration
                    </Link>
                  )}
                  <button
                    data-testid="drawer-logout"
                    onClick={async () => {
                      await logout();
                      setOpen(false);
                      navigate("/");
                    }}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-line px-4 py-3 text-sm font-bold text-rose-600"
                  >
                    <LogOut className="h-4 w-4" /> Déconnexion
                  </button>
                </>
              ) : (
                <div className="flex flex-col gap-2">
                  <Link
                    to="/connexion"
                    onClick={() => setOpen(false)}
                    data-testid="drawer-login"
                    className="rounded-xl bg-brand px-4 py-3 text-center text-sm font-bold text-white"
                  >
                    Se connecter
                  </Link>
                  <Link
                    to="/inscription"
                    onClick={() => setOpen(false)}
                    data-testid="drawer-register"
                    className="rounded-xl border border-line px-4 py-3 text-center text-sm font-bold text-ink"
                  >
                    Créer un compte
                  </Link>
                </div>
              )}
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

export default MobileHeader;
